import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePreferencesStore } from '../store/preferencesStore';
import { useToast } from './ui/Toast';
import ResizableSplitter from './ui/ResizableSplitter';
import WaitingGhost from './ui/WaitingGhost';
import {
  getFormDataFieldsFromRequest,
  normalizeFormDataFields,
  ensureTrailingEmptyFormDataRow,
} from '../utils/multipart';
import { normalizeRequestUrlAndParams } from '../utils/urlParams';
import RequestVariablesPanel from './RequestVariablesPanel';
import { setSelectedEnvironment } from '../store/variablesStore';
import { ensureTrailingEmptyKvRow } from '../utils/kvRows';
import { getRequestShortcutHints } from '../utils/keyboard';
import { normalizeAuth } from '../utils/requestAuth';
import { normalizeEditorTab, normalizeResponseTab, normalizeMock } from '../utils/workspaceTabPersistence';
import MockModal from './MockModal';
import { normalizeRequestBodyMode } from '../utils/requestBodyMode';
import { GQL_AUTO_FETCH_DEFAULT } from '../utils/graphqlSchema';
import { NO_FOLDER_VARS } from '../utils/folderVariables';
import { useRequestUrlPaste } from './request/useRequestUrlPaste';
import { useRequestSend } from './request/useRequestSend';
import { useRequestVariablesAndGraphql } from './request/useRequestVariablesAndGraphql';
import { useRequestKeyValueEditors } from './request/useRequestKeyValueEditors';
import { useRequestEditorSync } from './request/useRequestEditorSync';
import { useMockExamples } from './request/useMockExamples';
import {
  REQUEST_RESPONSE_SPLITTER_MIN_REQUEST_PX,
  REQUEST_RESPONSE_SPLITTER_MIN_RESPONSE_PX,
} from './request/requestBuilderLayout';
import { renderSseBuilder } from './request/renderSseBuilder';
import { renderGrpcBuilder } from './request/renderGrpcBuilder';
import { renderWebSocketBuilder } from './request/renderWebSocketBuilder';
import { renderRequestHeader } from './request/renderRequestHeader';
import { renderRequestEditorTabs } from './request/renderRequestEditorTabs';
import { renderRequestBodyPane } from './request/renderRequestBodyPane';
import { renderResponsePane } from './request/renderResponsePane';

const RequestBuilder = ({ 
  initialData,
  collectionName = null,
  collectionAuth = null,
  folderAuthChain = [],
  // Folder chain variables, nearest folder first (utils/folderVariables).
  folderVariables = NO_FOLDER_VARS,
  onDataChange, onUiStateChange, onSaveRequest, onAddToHistory, onUpdateResponse,
  history = [],
  onSelectFromHistory, onClearRequestHistory, selectedRequest, selectedEnvironment,
  workspaceVariables = [],
  globalVariables,
  collectionVariables = [],
  currentWorkspaceId,
  isTeamWorkspace = false,
  onVariableUpdate, // New prop for variable updates
  schemas, response, loading, onRegisterRenameShortcut, onRegisterLiveState, onRegisterSendHandler,
  onRequestNameCommit, onClearTabLoading,
  focusUrlNonce = 0,
  syncRevision = 0,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const editable = !readOnly;

  const emitDataChange = useCallback(
    (data) => {
      if (!editable) return;
      onDataChange?.(data);
    },
    [editable, onDataChange]
  );

  const { showToast } = useToast();
  const requestTabs = useMemo(
    () => [
      { key: 'params', label: t('request.params') },
      { key: 'authorization', label: t('request.authorization') },
      { key: 'headers', label: t('request.headers') },
      { key: 'cookies', label: t('request.cookies') },
      { key: 'body', label: t('request.body') },
      { key: 'script', label: t('request.script') },
    ],
    [t]
  );
  /** Hex colors aligned with index.css utility palette (same as httpMethodLabelClass). */
  const getMethodTextColor = (requestMethod) => {
    switch (requestMethod) {
      case 'GET':
        return '#16a34a';
      case 'POST':
        return '#ca8a04';
      case 'PUT':
        return '#ea580c';
      case 'DELETE':
        return '#dc2626';
      case 'PATCH':
        return '#7e22ce';
      case 'OPTIONS':
        return '#6b7280';
      case 'HEAD':
        return '#a38af4';
      default:
        return '#6b7280';
    }
  };

  const timeFormat = usePreferencesStore((s) => s.timeFormat);
  // Response-privacy gate for the "Explain" prompt (services/ai/responseAccess.js).
  const aiReadResponses = usePreferencesStore((s) => s.aiReadResponses === true);

  const [name, setName] = useState(initialData?.name || 'New Request');
  const [method, setMethod] = useState(initialData?.method || 'GET');
  const [url, setUrl] = useState(() => {
    const { displayUrl } = normalizeRequestUrlAndParams(
      initialData?.url,
      initialData?.params
    );
    return displayUrl;
  });
  const [urlImportNonce, setUrlImportNonce] = useState(0);
  const [headers, setHeaders] = useState(() =>
    ensureTrailingEmptyKvRow(
      initialData?.headers?.map((h, i) => ({ ...h, _id: h._id || `h-${i}-${Date.now()}` })) ?? [],
      'h'
    )
  );
  const [params, setParams] = useState(() => {
    const { paramRows } = normalizeRequestUrlAndParams(
      initialData?.url,
      initialData?.params
    );
    const ts = Date.now();
    return ensureTrailingEmptyKvRow(
      paramRows.map((p, i) => ({ ...p, _id: initialData?.params?.[i]?._id || `p-${i}-${ts}` })),
      'p'
    );
  });
  const [cookies, setCookies] = useState(() =>
    ensureTrailingEmptyKvRow(
      initialData?.cookies?.map((c, i) => ({ ...c, _id: c._id || `c-${i}-${Date.now()}` })) ?? [],
      'c'
    )
  );
  const [body, setBody] = useState(initialData?.body || '');
  const [bodyType, setBodyType] = useState(
    () => normalizeRequestBodyMode(initialData).bodyType
  );
  const [rawBodyLanguage, setRawBodyLanguage] = useState(
    () => normalizeRequestBodyMode(initialData).rawBodyLanguage
  );
  const [gqlAutoFetch, setGqlAutoFetch] = useState(GQL_AUTO_FETCH_DEFAULT);
  const [gqlSchemaStatus, setGqlSchemaStatus] = useState('idle');
  const [gqlSchemaError, setGqlSchemaError] = useState(null);
  const [gqlSchema, setGqlSchema] = useState(null);
  const [gqlFetchNonce, setGqlFetchNonce] = useState(0);
  const gqlAutoFetchedUrlRef = useRef('');
  const [formDataFields, setFormDataFields] = useState(() =>
    ensureTrailingEmptyFormDataRow(
      normalizeFormDataFields(getFormDataFieldsFromRequest(initialData)).map((row, i) => ({
        ...row,
        _id: row._id || `fd-${i}-${Date.now()}`,
      }))
    )
  );
  const formDataFileInputRef = useRef(null);
  const pendingFormDataFileIndexRef = useRef(null);
  const [auth, setAuth] = useState(() =>
    normalizeAuth(initialData?.auth, { forRequest: true })
  );
  const [preRequestScript, setPreRequestScript] = useState(
    initialData?.preRequestScript ?? ''
  );
  const [testScript, setTestScript] = useState(
    initialData?.testScript ?? ''
  );
  // Local Mock definitions for this request (response examples). Local-only.
  const [responseExamples, setResponseExamples] = useState(
    () => normalizeMock(initialData?.mock).responseExamples
  );
  const [showMockModal, setShowMockModal] = useState(false);
  const [mockUrlCopied, setMockUrlCopied] = useState(false);
  const [collapsedMockIds, setCollapsedMockIds] = useState(() => new Set());
  const toggleMockCollapsed = useCallback((id) => {
    setCollapsedMockIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const [testResults, setTestResults] = useState(null);
  const [testScriptErrors, setTestScriptErrors] = useState(null);
  const [activeTab, setActiveTab] = useState(() =>
    normalizeEditorTab(initialData?.editorTab)
  );
  const [responseViewerTab, setResponseViewerTab] = useState(() =>
    normalizeResponseTab(initialData?.responseTab)
  );
  const [scriptSubTab, setScriptSubTab] = useState('pre');
  const scriptSubTabs = useMemo(
    () => [
      { key: 'pre', label: t('request.scriptPre') },
      { key: 'post', label: t('request.scriptPost') },
    ],
    [t]
  );
  const [splitLayoutTick, setSplitLayoutTick] = useState(0);
  const onSplitLayoutEnd = useCallback(() => {
    setSplitLayoutTick((n) => n + 1);
  }, []);
  const [loadingElapsed, setLoadingElapsed] = useState(0);
  const inflightRequestRef = useRef({ method: '', url: '' });
  const cancelTokenRef = useRef(0);
  const onClearTabLoadingRef = useRef(onClearTabLoading);
  onClearTabLoadingRef.current = onClearTabLoading;
  const [editingRequestName, setEditingRequestName] = useState(false);
  const nameInputRef = useRef(null);
  const nameSnapshotRef = useRef('');
    // Ref to prevent update loops
  const lastTabIdRef = useRef(null);
  const nameRef = useRef(name);
  nameRef.current = name;
  const tabFieldsRef = useRef({
    name, method, url, headers, params, cookies, body, bodyType, rawBodyLanguage, formDataFields,
    auth, preRequestScript, testScript,
    mock: { responseExamples },
  });
  tabFieldsRef.current = {
    name, method, url, headers, params, cookies, body, bodyType, rawBodyLanguage, formDataFields,
    auth, preRequestScript, testScript,
    mock: { responseExamples },
  };

  const substituteVariablesRef = useRef(null);

  const notifyChange = useCallback(
    (partial) => {
      emitDataChange({ ...tabFieldsRef.current, ...partial });
    },
    [emitDataChange]
  );

  const {
    mockServerRunning, mockUrl, cloudMockEnabled, cloudMockUrl, addMockExample, updateMockExample,
    removeMockExample, duplicateMockExample, toggleMockExampleEnabled,
  } = useMockExamples({ url, initialData, setResponseExamples, notifyChange });

  const {
    urlInputRef, notifyParamsAndUrl, eventStream, isEventStreaming, cancelRequest, selectEditorTab,
    selectResponseViewerTab, commitRequestNameEdit,
  } = useRequestEditorSync({
    url, auth, response, body, initialData, onUiStateChange, onUpdateResponse, loading,
    onRegisterRenameShortcut, name, onRegisterLiveState, onRequestNameCommit, focusUrlNonce,
    syncRevision, emitDataChange, params, headers, cookies, setName, method, setMethod, setUrl,
    setUrlImportNonce, setHeaders, setParams, setCookies, setBody, bodyType, setBodyType,
    setRawBodyLanguage, setGqlSchemaStatus, setGqlSchema, setGqlSchemaError, setGqlFetchNonce,
    gqlAutoFetchedUrlRef, setFormDataFields, setAuth, setPreRequestScript, setTestScript,
    setResponseExamples, setTestResults, setTestScriptErrors, setActiveTab, setResponseViewerTab,
    setLoadingElapsed, cancelTokenRef, onClearTabLoadingRef, editingRequestName,
    setEditingRequestName, nameInputRef, nameSnapshotRef, lastTabIdRef, nameRef, tabFieldsRef,
  });

  useEffect(() => {
    if (!onRegisterSendHandler) return undefined;
    onRegisterSendHandler(async () => {
      const send = sendHttpRequestRef.current;
      if (!send) return null;
      return send();
    });
    return () => onRegisterSendHandler(null);
  }, [onRegisterSendHandler]);

  const {
    updateHeaders, updateParams, handleParamKeyChange, handleHeaderKeyChange, handleCookieKeyChange,
    removeHeader, removeParam, updateCookies, removeCookie, updateFormDataFields,
    removeFormDataField, pickFormDataFile, handleFormDataFileInput,
  } = useRequestKeyValueEditors({
    url, auth, body, name, emitDataChange, showToast, params, headers, cookies, method, setHeaders,
    setParams, setCookies, bodyType, formDataFields, setFormDataFields, formDataFileInputRef,
    pendingFormDataFileIndexRef, notifyChange, notifyParamsAndUrl,
  });

  const {
    scriptLocalVarsRef, variablesPanelOpen, setVariablesPanelOpen, allEnvironments, storedScopes,
    resolveVariableScope, substituteVariables, buildUrl, buildScriptScopes, buildEnvVariableMap,
    applyScriptEnvMutations, handleBodyTypeChange, doGqlIntrospect,
  } = useRequestVariablesAndGraphql({
    url, body, folderVariables, selectedEnvironment, workspaceVariables, globalVariables,
    collectionVariables, showToast, params, headers, method, setMethod, setHeaders, setBody,
    bodyType, setBodyType, rawBodyLanguage, gqlAutoFetch, setGqlSchemaStatus, setGqlSchema,
    setGqlSchemaError, gqlFetchNonce, setGqlFetchNonce, gqlAutoFetchedUrlRef, tabFieldsRef,
    substituteVariablesRef, notifyChange,
  });

  const sendHttpRequestRef = useRef(null);
  const {
    showSendDropdown, setShowSendDropdown, sendDropdownRef, sendDropdownTriggerRef,
    sendDropdownPosition, showHistoryDropdown, setShowHistoryDropdown, historyDropdownRef,
    historyDropdownTriggerRef, historyDropdownPosition, activeHistoryId, historyPreview,
    requestHistoryKey, requestHistory, exitHistoryPreview, enterHistoryPreview, sendHttpRequest,
    downloadResponse, saveRequest,
  } = useRequestSend({
    t, url, auth, response, body, preRequestScript, initialData, collectionAuth, folderAuthChain,
    onSaveRequest, onAddToHistory, onUpdateResponse, history, onSelectFromHistory, loading, name,
    editable, showToast, params, headers, cookies, method, bodyType, rawBodyLanguage,
    formDataFields, setAuth, testScript, responseExamples, setTestResults, setTestScriptErrors,
    inflightRequestRef, cancelTokenRef, lastTabIdRef, tabFieldsRef, substituteVariables,
    notifyChange, sendHttpRequestRef, scriptLocalVarsRef, buildUrl, buildScriptScopes,
    buildEnvVariableMap, applyScriptEnvMutations,
  });

  const { handleUrlPaste, authVariableProps, updateAuth } = useRequestUrlPaste({
    t, folderVariables, initialData, selectedEnvironment, workspaceVariables, globalVariables,
    collectionVariables, currentWorkspaceId, onVariableUpdate, showToast, setMethod, setUrl,
    setUrlImportNonce, setHeaders, setParams, setCookies, setBody, setBodyType, setRawBodyLanguage,
    setFormDataFields, setAuth, notifyChange,
  });

  const shortcutHints = getRequestShortcutHints();
  const breadcrumbDisplayLabel = name?.trim() ? name : t('request.newRequest');
  const breadcrumbWidthSource = editingRequestName ? (name ?? '') : breadcrumbDisplayLabel;
  const nameSlotWidthCh = Math.min(Math.max(breadcrumbWidthSource.length, 12), 48);
  // While previewing history, let the name slot shrink to its text so the chip sits right
  // beside the name (same gap as between the chevron and the name).
  const nameSlotWidth = historyPreview && !editingRequestName ? 'auto' : `${nameSlotWidthCh}ch`;

  // Shared loader UI — used both in the no-response fallback and passed into ResponseViewer.
  const inflightUrl = inflightRequestRef.current.url.replace(/^https?:\/\//, '');
  const loaderNode = (
    <WaitingGhost
      title="Awaiting response"
      meta={
        <>
          {inflightRequestRef.current.method}&nbsp;
          {inflightUrl.slice(0, 60)}
          {inflightUrl.length > 60 ? '...' : ''}
          &nbsp;&middot;&nbsp;{loadingElapsed.toFixed(1)}s
        </>
      }
      onCancel={cancelRequest}
      cancelLabel="Cancel"
    />
  );

  // ── SSE requests: render a dedicated builder ────────────────────────────
  if (initialData?.type === 'sse') {
    return (
      renderSseBuilder({ collectionName, t, nameSlotWidthCh, editingRequestName, nameInputRef, name, setName, emitDataChange, method, url, headers, params, cookies, body, bodyType, auth, commitRequestNameEdit, nameSnapshotRef, setEditingRequestName, breadcrumbDisplayLabel, readOnly, saveRequest, initialData, tabFieldsRef, selectedEnvironment, workspaceVariables, globalVariables, collectionVariables, folderVariables })
    );
  }

  // ── gRPC requests: render a dedicated builder ────────────────────────────
  if (initialData?.type === 'grpc') {
    return (
      renderGrpcBuilder({ collectionName, t, nameSlotWidthCh, editingRequestName, nameInputRef, name, setName, emitDataChange, method, url, headers, params, cookies, body, bodyType, auth, commitRequestNameEdit, nameSnapshotRef, setEditingRequestName, breadcrumbDisplayLabel, readOnly, saveRequest, initialData, storedScopes, tabFieldsRef })
    );
  }

  // ── WebSocket requests: render a dedicated builder ──────────────────────
  if (initialData?.type === 'websocket') {
    return (
      renderWebSocketBuilder({ collectionName, t, nameSlotWidthCh, editingRequestName, nameInputRef, name, setName, emitDataChange, method, url, headers, params, cookies, body, bodyType, auth, commitRequestNameEdit, nameSnapshotRef, setEditingRequestName, breadcrumbDisplayLabel, readOnly, saveRequest, initialData, storedScopes, tabFieldsRef })
    );
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col">
      {/* Header */}
      {renderRequestHeader({ collectionName, t, nameSlotWidth, editingRequestName, nameInputRef, name, setName, emitDataChange, method, url, headers, params, cookies, body, bodyType, auth, commitRequestNameEdit, nameSnapshotRef, setEditingRequestName, readOnly, breadcrumbDisplayLabel, historyPreview, timeFormat, exitHistoryPreview, initialData, saveRequest, getMethodTextColor, editable, setMethod, urlInputRef, setUrl, tabFieldsRef, setParams, sendHttpRequest, handleUrlPaste, urlImportNonce, selectedEnvironment, workspaceVariables, globalVariables, collectionVariables, folderVariables, onVariableUpdate, currentWorkspaceId, historyDropdownTriggerRef, setShowHistoryDropdown, showHistoryDropdown, historyDropdownPosition, historyDropdownRef, requestHistory, onClearRequestHistory, requestHistoryKey, activeHistoryId, enterHistoryPreview, isEventStreaming, loading, sendDropdownTriggerRef, setShowSendDropdown, showSendDropdown, sendDropdownPosition, sendDropdownRef, downloadResponse })}

      {/* Resizable Split Layout */}
      <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
        <ResizableSplitter
          direction="vertical"
          initialSplit={35}
          minSize={0}
          maxSize={100}
          minFirstPanelPx={REQUEST_RESPONSE_SPLITTER_MIN_REQUEST_PX}
          minSecondPanelPx={REQUEST_RESPONSE_SPLITTER_MIN_RESPONSE_PX}
          className="h-full"
          onResizeEnd={onSplitLayoutEnd}
        >
          {/* Request Section */}
          <div
            className="bg-white flex flex-col h-full"
            style={{ minHeight: 0, pointerEvents: readOnly ? 'none' : 'auto', opacity: readOnly ? 0.92 : 1 }}
          >
            {/* Tabs */}
            <div className="flex-shrink-0 border-b">
              <nav className="flex items-center space-x-1 px-5">
                {requestTabs.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => selectEditorTab(key)}
                    className={`py-0.5 px-2.5 border-b-2 font-medium text-[11px] rounded bg-transparent transition-colors duration-150 ${
                      activeTab === key
                        ? 'text-blue-600 border-blue-500'
                        : 'text-gray-500 hover:text-gray-700 hover:border-gray-300 border-transparent'
                    }`}
                  >
                    {label}
                  </button>
                ))}
                {/* The variables toggle used to sit here. It is now
                    RequestVariablesToggle, beside Save in the header row. */}
                <button
                  type="button"
                  onClick={() => setShowMockModal(true)}
                  className="py-0.5 px-2.5 border-b-2 border-transparent font-medium text-[11px] rounded bg-transparent text-gray-500 hover:text-gray-700 transition-colors duration-150"
                >
                  {t('request.mock')}
                </button>
              </nav>
            </div>            {/* Tab Content - Scrollable. Body and Script scroll inside their
                own editors; every other tab (Params, Headers, Auth, …) scrolls
                through OsScroll — native `panel-scroll` reverts to the wide
                system bar on macOS hover (see ui/OsScroll.js). */}
            {activeTab !== 'body' && (
            renderRequestEditorTabs({ activeTab, params, updateParams, removeParam, t, handleParamKeyChange, selectedEnvironment, workspaceVariables, globalVariables, collectionVariables, folderVariables, onVariableUpdate, initialData, currentWorkspaceId, auth, updateAuth, collectionName, authVariableProps, headers, updateHeaders, removeHeader, handleHeaderKeyChange, cookies, updateCookies, removeCookie, handleCookieKeyChange, scriptSubTabs, setScriptSubTab, scriptSubTab, splitLayoutTick, preRequestScript, setPreRequestScript, notifyChange, sendHttpRequest, testScript, setTestScript })
            )}
            {/* Body Tab — kept mounted once opened, hidden on the other tabs: rebuilding
                its Monaco model on every visit took a second with a 10 MB body (Postman
                keeps it too). See request/KeptTabPane. */}
            {renderRequestBodyPane({ activeTab, bodyType, formDataFileInputRef, handleFormDataFileInput, handleBodyTypeChange, t, rawBodyLanguage, setRawBodyLanguage, notifyChange, body, setBody, gqlAutoFetch, setGqlAutoFetch, gqlSchemaStatus, doGqlIntrospect, url, gqlSchemaError, gqlSchema, splitLayoutTick, selectedEnvironment, globalVariables, collectionVariables, folderVariables, workspaceVariables, onVariableUpdate, initialData, currentWorkspaceId, formDataFields, updateFormDataFields, removeFormDataField, authVariableProps, pickFormDataFile, sendHttpRequest })}
          </div>

          {/* Response Section — tabs + status share the top bar (no separate "Response" title) */}
          {renderResponsePane({ response, testResults, selectResponseViewerTab, responseViewerTab, initialData, method, url, name, aiReadResponses, eventStream, loading, selectedEnvironment, globalVariables, splitLayoutTick, loaderNode, testScriptErrors, tabFieldsRef, sendHttpRequest, t, shortcutHints })}
        </ResizableSplitter>
      </div>      {/* A floating companion, not a tab: you want it open WHILE editing a
          URL, and a tab would replace the thing you are looking at. Mounted
          here beside the other overlay so it sits above the builder without
          taking part in its layout. */}
      {/* The scope lists are passed through because a value cell highlights its
          own `{{…}}` with the same component the URL uses — without them every
          nested reference would render as missing. */}
      <RequestVariablesPanel
        open={variablesPanelOpen}
        onClose={() => setVariablesPanelOpen(false)}
        requestFields={tabFieldsRef.current}
        resolveScope={resolveVariableScope}
        environments={allEnvironments}
        selectedEnvironment={selectedEnvironment}
        onSelectEnvironment={setSelectedEnvironment}
        onVariableUpdate={onVariableUpdate}
        workspaceVariables={workspaceVariables}
        globalVariables={globalVariables}
        collectionVariables={collectionVariables}
        folderVariables={folderVariables}
        variableContext={{
          collectionId: initialData?.collectionId,
          workspaceId: currentWorkspaceId,
        }}
      />

      <MockModal
        isOpen={showMockModal}
        onClose={() => setShowMockModal(false)}
        responseExamples={responseExamples}
        collapsedMockIds={collapsedMockIds}
        mockUrlCopied={mockUrlCopied}
        setMockUrlCopied={setMockUrlCopied}
        mockUrl={mockUrl}
        inCollection={Boolean(initialData?.collectionId)}
        mockServerRunning={mockServerRunning}
        cloudMockEnabled={cloudMockEnabled}
        cloudMockUrl={cloudMockUrl}
        schemas={schemas}
        splitLayoutTick={splitLayoutTick}
        addMockExample={addMockExample}
        updateMockExample={updateMockExample}
        removeMockExample={removeMockExample}
        duplicateMockExample={duplicateMockExample}
        toggleMockExampleEnabled={toggleMockExampleEnabled}
        toggleMockCollapsed={toggleMockCollapsed}
        t={t}
      />
    </div>
  );
};

export default RequestBuilder;
